/**
 * App notifications: the key a device subscribes with, and telling the
 * server which devices to notify.
 *
 * The notification itself (a weather warning's headline, beside its email)
 * is sent by the backend (services/alerts/pushService); the browser's side
 * of subscribing is shared/lib/pushNotifications.
 *
 * Endpoints (every answer also carries `success`):
 * - GET    /push/public-key → { publicKey: string | null } (null: the server
 *   cannot send notifications yet, so the switch is not offered).
 * - POST   /push/subscription { endpoint, keys: { p256dh, auth } } → {}.
 * - DELETE /push/subscription { endpoint } → { removed }.
 *
 * Who uses it: shared/lib/pushNotifications. Auth: the signed-in person's own devices.
 *
 * @module push
 */

import { apiClient } from './apiClient';

/** A device's subscription as the browser gives it (PushSubscription.toJSON()). */
export interface DeviceSubscription {
  /** Where the device's push service takes a message. */
  endpoint: string;
  /** The keys a message to it is encrypted with (base64url). */
  keys: { p256dh: string; auth: string };
}

/**
 * The server's public key for subscribing, or null when it cannot send notifications.
 *
 * @returns The key (base64url), or null.
 */
export async function getPushPublicKey(): Promise<string | null> {
  const response = await apiClient.get<{ success: boolean; publicKey: string | null }>('/push/public-key');
  return response.data.publicKey ?? null;
}

/**
 * Tells the server to notify this device.
 *
 * @param subscription - This device's, from the browser.
 */
export async function savePushSubscription(subscription: DeviceSubscription): Promise<void> {
  await apiClient.post('/push/subscription', subscription);
}

/**
 * Tells the server to stop notifying this device.
 *
 * @param endpoint - This device's endpoint.
 */
export async function deletePushSubscription(endpoint: string): Promise<void> {
  await apiClient.delete('/push/subscription', { data: { endpoint } });
}
